/**
 * =============================================================================
 * useFeatureFlag.ts - Hook per verificare i feature flag del tenant
 * =============================================================================
 * 
 * Carica la configurazione del tenant corrente dal database e permette
 * di verificare se un feature flag è abilitato.
 * 
 * Utilizza React Query con staleTime: Infinity (la config non cambia in sessione).
 * 
 * @version 1.0.0 - Feature Flags Phase 2
 * =============================================================================
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useTenantId } from "@/hooks/useTenantId";
import type { FeatureFlag } from "@/config/featureFlags";
import { DEFAULT_FEATURE_FLAGS } from "@/config/featureFlags";

/**
 * Hook che carica la configurazione JSONB del tenant corrente.
 * Restituisce l'oggetto config o {} se non disponibile.
 */
export function useTenantConfig(): Record<string, unknown> {
  const { user } = useAuth();
  const tenantId = useTenantId();

  const { data: config } = useQuery({
    queryKey: ["tenant_config", tenantId],
    queryFn: async () => {
      if (!tenantId) return {};
      const { data, error } = await supabase
        .from("tenants")
        .select("config")
        .eq("id", tenantId)
        .single();
      if (error) {
        console.error("[useTenantConfig] Error:", error);
        return {};
      }
      return (data?.config as Record<string, unknown>) ?? {};
    },
    enabled: !!user && !!tenantId,
    staleTime: Infinity, // La config tenant non cambia durante la sessione
  });

  return config ?? {};
}

/**
 * Hook che verifica se un singolo feature flag è abilitato per il tenant corrente.
 * 
 * @param flag - Il feature flag da verificare
 * @returns true se il flag è abilitato, false altrimenti (default: false)
 * 
 * @example
 * const quizEnabled = useFeatureFlag("feature_quiz_engine");
 * if (!quizEnabled) return <Navigate to="/dashboard" />;
 */
export function useFeatureFlag(flag: FeatureFlag): boolean {
  const config = useTenantConfig();
  const value = config[flag];
  
  // Se il valore è presente nel config del tenant, usalo
  if (typeof value === "boolean") return value;
  
  // Altrimenti usa il default (false per nuovi tenant)
  return DEFAULT_FEATURE_FLAGS[flag];
}

/**
 * Hook che verifica multipli feature flag contemporaneamente.
 * 
 * @param flags - Array di feature flag da verificare
 * @returns Record con lo stato di ogni flag
 * 
 * @example
 * const flags = useFeatureFlags(["feature_quiz_engine", "feature_certificates"]);
 * if (flags.feature_quiz_engine) { ... }
 */
export function useFeatureFlags(flags: FeatureFlag[]): Record<FeatureFlag, boolean> {
  const config = useTenantConfig();
  
  const result = {} as Record<FeatureFlag, boolean>;
  for (const flag of flags) {
    const value = config[flag];
    result[flag] = typeof value === "boolean" ? value : DEFAULT_FEATURE_FLAGS[flag];
  }
  return result;
}
